/**
 * What the milk sales tickets form needs and the browser does not give: the morning job's reasons in
 * plain words, a held load turned into fields a person can correct, and those fields turned back into
 * the confirmation the API takes.
 *
 * The checks mirror the API's (`api/app/services/milk_sales.py` and `planner.persistence.sales.check_load`)
 * so a mistake is named at its field before anything is sent; the API's answer is still the one that
 * counts. Times are typed and shown in the planner's zone (`time_zone` of the held list), through
 * `wallToInstant`, as the road closures do.
 */
import type { ConfirmBody, HeldImport } from "./api";
import { instantToWall, wallToInstant } from "./closureForm";
import { isCalendarDate } from "./time";

/** One reason, as the milk office would say it. The job's own sentence is kept when no pattern
 *  matches, so nothing it says is lost. */
export function plainProblem(problem: string): string {
  let m = /^location code\(s\) not known for this haulier: (.*)$/.exec(problem);
  if (m) {
    return (
      `The haulier's farm code is new to the planner: ${m[1]}. Choose the farm on its line; ` +
      "the code is then remembered for the next tickets."
    );
  }
  m = /^figure\(s\) read with less than ([\d.]+) confidence: (.*)$/.exec(problem);
  if (m) return `The scan was hard to read at ${m[2]}. Check these figures against the ticket.`;
  m = /^(.*?): the farm litres add up to (\d+), the ticket prints (\d+)$/.exec(problem);
  if (m) {
    return (
      `${m[1]}: the farms add up to ${number(m[2])} L, but the ticket's total is ${number(m[3])} L. ` +
      "One farm's litres were probably misread."
    );
  }
  m = /^(.*?): no transfer into a tanker read$/.exec(problem);
  if (m) return `${m[1]}: no transfer into the buyer's tanker could be read, so the load's day is not known. Fill it in.`;
  m = /^(.*?): (\d+) L collected and not on board, (\d+) L transferred: more than ([\d.]+)% apart$/.exec(problem);
  if (m) {
    return (
      `${m[1]}: ${number(m[2])} L was collected but ${number(m[3])} L went into the buyer's tanker, ` +
      `more than the ${m[4]}% the meters may differ.`
    );
  }
  m = /^(.*?): 'Milk collected' not read$/.exec(problem);
  if (m) return `${m[1]}: the ticket's total ("Milk collected") could not be read.`;
  m = /^(.*?): no farm collection read$/.exec(problem);
  if (m) return `${m[1]}: no farm collection could be read.`;
  m = /^the milk went into (\d+) tanks \((.*)\)/.exec(problem);
  if (m) return `The milk went into ${m[1]} tanks (${m[2]}). Keep only the farms whose milk is in this load.`;
  m = /^(\d+) L stayed on board (\S+): not all of its farms' milk went to (.*)$/.exec(problem);
  if (m) {
    return (
      `${number(m[1])} L stayed on lorry ${m[2]}, so not all of its farms' milk went to ${m[3]}. ` +
      "A farm's milk goes to one buyer a day: keep only the farms in this load."
    );
  }
  m = /^delivery record of (\d+) L matches no transfer on the Summary Print$/.exec(problem);
  if (m) return `The delivery note's ${number(m[1])} L matches no transfer on the ticket.`;
  if (problem === "no Optiload Summary Print found") return "No Optiload ticket was found in the attachments.";
  if (problem === "no farm collection read") return "No farm collection could be read.";
  return problem;
}

const grouped = new Intl.NumberFormat("en-GB");
const number = (digits: string | undefined) => grouped.format(Number(digits));

/** `6,200`, `6200` or `6200.5` as a number; null for anything else. */
export function parseLitres(text: string): number | null {
  const raw = text.trim().replace(/,(?=\d{3}\b)/g, "");
  if (!/^\d+(\.\d)?$/.test(raw)) return null;
  return Number(raw);
}

export interface LineDraft {
  /** Stable for React and for error keys while lines are added and removed. */
  key: string;
  producerId: number | null;
  farmAsRead: string | null;
  locationCode: string | null;
  codeKnown: boolean;
  /** Remember the code as this farm's, for a code the planner does not know. */
  remember: boolean;
  /** `YYYY-MM-DDTHH:MM` in the planner's zone, as a datetime-local input holds it. */
  collectedAt: string;
  /** The time as read, to the second, sent back unchanged while `collectedAt` still shows it: a
   *  datetime-local input holds minutes, and the ticket's seconds are not the person's to drop. */
  readAt: { wall: string; instant: string } | null;
  litres: string;
  ticketNumber: string;
  vehicleRegistration: string;
}

let lastKey = 0;
export const newKey = () => `line-${++lastKey}`;

export function draftsFrom(imp: HeldImport, timeZone: string): LineDraft[] {
  return imp.proposed.lines.map((line) => ({
    key: newKey(),
    producerId: line.producer_id,
    farmAsRead: line.farm_as_read,
    locationCode: line.location_code,
    codeKnown: line.code_known,
    remember: !line.code_known && line.location_code !== null,
    collectedAt: line.collected_at ? instantToWall(line.collected_at, timeZone) : "",
    readAt: line.collected_at
      ? { wall: instantToWall(line.collected_at, timeZone), instant: line.collected_at }
      : null,
    litres: line.litres !== null ? String(line.litres) : "",
    ticketNumber: line.ticket_number ?? "",
    vehicleRegistration: line.vehicle_registration ?? "",
  }));
}

export function emptyDraft(): LineDraft {
  return {
    key: newKey(),
    producerId: null,
    farmAsRead: null,
    locationCode: null,
    codeKnown: true,
    remember: false,
    collectedAt: "",
    readAt: null,
    litres: "",
    ticketNumber: "",
    vehicleRegistration: "",
  };
}

export type LineField = "farm" | "collectedAt" | "litres" | "ticketNumber";
/** `loadDate`, `lines`, or `<line key>.<field>`. */
export type Errors = Record<string, string>;

/** The total of the litres typed so far, ignoring a line that is not yet a number. */
export function totalLitres(drafts: LineDraft[]): number {
  return Math.round(drafts.reduce((sum, d) => sum + (parseLitres(d.litres) ?? 0), 0) * 10) / 10;
}

export function buildConfirmation(
  drafts: LineDraft[],
  loadDate: string,
  note: string,
  timeZone: string,
): { body: ConfirmBody } | { errors: Errors } {
  const errors: Errors = {};
  if (!isCalendarDate(loadDate)) errors.loadDate = "The day of the load is needed";
  if (drafts.length === 0) errors.lines = "A load needs at least one farm";
  const seen = new Map<string, string>();
  const codes = new Map<string, number>();
  const lines = drafts.map((d, i) => {
    const at =
      d.readAt && d.collectedAt === d.readAt.wall
        ? d.readAt.instant
        : d.collectedAt
          ? wallToInstant(d.collectedAt, timeZone)
          : null;
    const litres = parseLitres(d.litres);
    if (d.producerId === null) errors[`${d.key}.farm`] = `Line ${i + 1}: choose the farm`;
    if (!at) errors[`${d.key}.collectedAt`] = `Line ${i + 1}: the time of collection is needed`;
    if (litres === null || litres <= 0) {
      errors[`${d.key}.litres`] = `Line ${i + 1}: litres must be a number above 0, at most one decimal`;
    }
    const twice = `${d.producerId}|${d.ticketNumber.trim()}`;
    if (d.producerId !== null && seen.has(twice)) {
      errors[`${d.key}.ticketNumber`] = `Line ${i + 1}: the same farm and ticket as ${seen.get(twice)}`;
    }
    seen.set(twice, `line ${i + 1}`);
    if (d.remember && d.locationCode && d.producerId !== null && !d.codeKnown) {
      const before = codes.get(d.locationCode);
      if (before !== undefined && before !== d.producerId) {
        errors[`${d.key}.farm`] = `Line ${i + 1}: code ${d.locationCode} is on another line for another farm`;
      }
      codes.set(d.locationCode, d.producerId);
    }
    return {
      producer_id: d.producerId ?? 0,
      collected_on: d.collectedAt.slice(0, 10),
      collected_at: at,
      litres: litres ?? 0,
      ticket_number: d.ticketNumber.trim() || null,
      vehicle_registration: d.vehicleRegistration.trim() || null,
      location_code: d.locationCode,
    };
  });
  if (Object.keys(errors).length > 0) return { errors };
  return {
    body: {
      load_date: loadDate,
      lines,
      map_codes: [...codes].map(([code, producer_id]) => ({ code, producer_id })),
      note: note.trim() || null,
    },
  };
}

/** A milk price in pence per litre with two decimals, as the trade quotes it: 0.4 £/L is `40.00 ppl`
 *  (Ronald, 19 October 2026). The API keeps pounds per litre. */
export function formatPrice(gbpPerLitre: number): string {
  return `${(gbpPerLitre * 100).toFixed(2)} ppl`;
}

/** A price typed in pence per litre (`41`, `41.50`, `41p`, `41 ppl`) or in pounds (`£0.41`, `0.41`), as pounds
 *  per litre; null when it is neither, or finer than 0.01 pence: the invoice multiplies the stored price, so a
 *  third decimal of a penny would round differently on the screen, the invoice and in Xero (Ronald, 19 October
 *  2026). A bare number of 1 or more is pence: no milk sells at £1 a litre or more (the API refuses 2), and none
 *  below 1p. */
export function parsePrice(text: string): number | null {
  const raw = text.trim().toLowerCase();
  // Two decimals in pence, so four in pounds.
  const fromPence = (digits: string) => (/^\d+(?:\.\d{1,2})?$/.test(digits) ? Number((Number(digits) / 100).toFixed(4)) : null);
  const fromPounds = (digits: string) => (/^\d*\.\d{1,4}$|^\d+$/.test(digits) ? Number(digits) : null);
  const pounds = /^£\s*(\d*\.?\d+)$/.exec(raw);
  if (pounds) return fromPounds(pounds[1]!);
  const pence = /^(\d+(?:\.\d+)?)\s*(?:p|ppl)$/.exec(raw);
  if (pence) return fromPence(pence[1]!);
  if (/^\d*\.\d+$|^\d+$/.test(raw)) return Number(raw) >= 1 ? fromPence(raw) : fromPounds(raw);
  return null;
}
